/**
 * GroupVariant — sidebar content for focus group rooms (2+ participants).
 *
 * Pane order (Step 11c, SPEC section 8): the room title (inline-editable by its
 * owner), Who's talking (circles, always open; each circle opens the
 * twin's profile card), Topics, Materials, the audience banner with the
 * discussion brief under it, Ask about (open while the room is empty). Empty
 * sections are hidden.
 *
 * Collapsed state is owned by ChatSidebar; this variant always renders
 * its full expanded content.
 */

import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { CollapsibleText, SidebarDivider } from '@/components/chat/SidebarPrimitives'
import { PaneSection } from '@/components/chat/PaneSection/PaneSection'
import { twinsApi, FeaturedTwinDetail } from '@/lib/api'
import { getProfileCardStarters } from '@/lib/utils/twin-blurb'
import { TellerPicksController } from '@/hooks/useTellerPicks'

import {
  ChatRoomDto,
  ChatRoomParticipantDto,
  ChatRoomParticipantState,
  ChatRoomSettings,
  ChatRoomStatus,
} from '@digital-twins/shared-types'

import { RoomSettingsPanel } from './RoomSettingsPanel'
import { RoomTitleEditor } from './RoomTitleEditor/RoomTitleEditor'
import { AudienceBanner } from './AudienceBanner/AudienceBanner'

interface GroupVariantProps {
  room: ChatRoomDto
  participants: Array<ChatRoomParticipantDto>
  participantStates: Record<string, ChatRoomParticipantState>
  roomStatus: ChatRoomStatus
  roomTitle?: string | null
  /** The viewer owns the room: only then is the title editable. */
  viewerIsOwner?: boolean
  onRename?: (title: string) => unknown
  /** The viewer can read the room's audience (owners, org collaborators); false skips the banner fetch. */
  canFetchAudience?: boolean
  onSettingsChange?: (partial: Partial<ChatRoomSettings>) => void
  canUpdateSettings?: boolean
  isSavingSettings?: boolean
  settingsError?: string | null
  onSend?: (text: string) => void
  /** Disables the "Ask about" starters (e.g. while a guest ask-about turn is pending). */
  startersDisabled?: boolean
  collapsed?: boolean
  /** Circle states and picks from the page (Step 11b); read-only when absent. */
  tellerPicks?: TellerPicksController
  /** The profile card's Ask action; absent hides it. */
  onAskParticipant?: (participant: ChatRoomParticipantDto) => void
  /** Topics section, or null when the room has none (built by ChatSidebar). */
  topicsSection?: ReactNode
  /** Materials section, or null when the room has none. */
  materialsSection?: ReactNode
  /** Ask about starts open while the room has no message. */
  hasMessages?: boolean
}

const MAX_STARTERS_DISPLAYED = 8

export function GroupVariant(props: GroupVariantProps) {
  const {
    room,
    participants,
    roomTitle,
    viewerIsOwner = false,
    onRename,
    canFetchAudience = false,
    onSettingsChange,
    canUpdateSettings,
    isSavingSettings,
    settingsError,
    onSend,
    startersDisabled = false,
    tellerPicks,
    onAskParticipant,
    topicsSection = null,
    materialsSection = null,
    hasMessages = false,
  } = props

  const { t } = useTranslation('app')

  const brief = room.settings?.topics?.trim() ?? ''
  const hasBrief = brief.length > 0
  const askAboutSectionId = hasMessages ? 'ask-about' : 'ask-about-empty-room'

  const { starters, isLoading: startersLoading } = useGroupStarters(
    room.populationId,
    participants,
    true,
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <RoomTitleEditor
          title={roomTitle ?? room.title}
          isEditable={viewerIsOwner}
          onRename={onRename}
        />
      </div>

      {room.settings && (
        <RoomSettingsPanel
          settings={room.settings}
          participants={participants}
          populationId={room.populationId}
          tellerPicks={tellerPicks}
          onAskParticipant={onAskParticipant}
          canUpdateSettings={canUpdateSettings}
          isSaving={isSavingSettings}
          error={settingsError}
          collapsed={false}
        />
      )}

      {topicsSection && (
        <>
          <SidebarDivider />
          {topicsSection}
        </>
      )}

      {materialsSection && (
        <>
          <SidebarDivider />
          {materialsSection}
        </>
      )}

      {(room.populationName || (canFetchAudience && room.populationId) || hasBrief) && (
        <>
          <SidebarDivider />
          {/* Always visible, never collapsible: the banner, then the brief. */}
          <section className="flex flex-col gap-3" data-testid="chat-sidebar-audience">
            <AudienceBanner
              populationId={room.populationId}
              populationName={room.populationName}
              canFetchAudience={canFetchAudience}
            />
            {hasBrief && (
              <CollapsibleText label={t('roomPane.brief')} text={brief} maxLines={4} />
            )}
          </section>
        </>
      )}

      {starters.length > 0 ? (
        <>
          <SidebarDivider />
          {/* Keyed by its id: the first send switches the section, and its open state with it. */}
          <PaneSection
            key={askAboutSectionId}
            sectionId={askAboutSectionId}
            title={t('roomPane.askAbout')}
            isDefaultOpen={!hasMessages}
            dataTestId="chat-sidebar-ask-about-section"
          >
            <div data-testid="chat-sidebar-ask-about" className="flex flex-col gap-1.5">
              {starters.map((starter, index) => (
                <button
                  key={starter}
                  data-testid={`chat-sidebar-starter-${index}`}
                  type="button"
                  disabled={startersDisabled}
                  onClick={() => onSend?.(starter)}
                  className="rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm bg-neutral-200 px-3 py-2 text-left text-sm leading-[1.4] text-twin-chat-fg transition-colors hover:bg-neutral-300 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:disabled:hover:bg-neutral-700"
                >
                  {starter}
                </button>
              ))}
            </div>
          </PaneSection>
        </>
      ) : startersLoading ? (
        <>
          <SidebarDivider />
          <div className="flex flex-col gap-2 animate-pulse">
            <div className="h-3 w-20 rounded bg-twin-chat-bubble/40" />
            <div className="flex flex-col gap-1.5">
              <div className="h-9 w-full rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm bg-twin-chat-bubble/40" />
              <div className="h-9 w-5/6 rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm bg-twin-chat-bubble/40" />
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// useGroupStarters — aggregate & deduplicate conversation starters
// ---------------------------------------------------------------------------

function useGroupStarters(
  populationId: string | null | undefined,
  participants: Array<ChatRoomParticipantDto>,
  enabled: boolean,
): { starters: Array<string>; isLoading: boolean } {
  const keys = participants.map((p) =>
    enabled && populationId && p.featuredTwinId ? `twin-${populationId}-${p.personaId}` : null,
  )

  const results = keys.map((key, i) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSWR<FeaturedTwinDetail>(
      key,
      () => twinsApi.getFeaturedTwin(populationId!, participants[i].personaId),
      { revalidateOnFocus: false, errorRetryCount: 1 },
    ),
  )

  const twinData = results.map((r) => r.data)
  const anyLoading = results.some((r) => r.isLoading)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const starters = useMemo(() => {
    const seen = new Set<string>()
    const out: Array<string> = []
    for (const twin of twinData) {
      for (const s of getProfileCardStarters(twin ?? null)) {
        const normalised = s.trim()
        if (!seen.has(normalised)) {
          seen.add(normalised)
          out.push(normalised)
        }
      }
    }
    if (out.length === 0) {
      for (const p of participants) {
        for (const s of p.conversationStarters ?? []) {
          const normalised = s.trim()
          if (!seen.has(normalised)) {
            seen.add(normalised)
            out.push(normalised)
          }
        }
      }
    }
    return out.slice(0, MAX_STARTERS_DISPLAYED)
  }, [twinData, participants])

  return { starters, isLoading: anyLoading }
}
